import React, { useId } from 'react';
import styled from 'styled-components';

import {
  GrowthPlannerHandoffResult,
  GrowthPlannerHandoffStatus,
  ResponseTone,
  SupportAction
} from '../../../types';
import HighlightedText from '../../HighlightedText';
import { ResponseActionButton } from '../../ResponseActionButton';
import {
  responseTone,
  responseType,
  rsColor,
  rsFor,
  rsHandoffColor,
  rsv
} from '../../responseScale';

/** 6473:11916 / 11958: the sent and failed cards' badge; sending draws the ring instead. */
const BADGE: Partial<
  Record<GrowthPlannerHandoffStatus, { glyph: string; ink: string; fill: string }>
> = {
  sent: { glyph: '✓', ink: rsHandoffColor.sent, fill: rsHandoffColor.sentFill },
  failed: { glyph: '!', ink: rsHandoffColor.failed, fill: rsHandoffColor.failedFill }
};

interface IResultButtonLook {
  fill: string;
  ink: string;
  padding: 'handoffButton' | 'handoffRetryButton';
}

/** The two result buttons draw on the hand-off's hues, not the tone scale's. */
const BUTTON_LOOK: Partial<Record<ResponseTone, IResultButtonLook>> = {
  // 6473:11948: View in Growth Planner, 214 x 41.
  accent: { fill: rsHandoffColor.view, ink: rsColor.white, padding: 'handoffButton' },
  // 6473:11968: Retry, 154 x 43, dark ink.
  error: {
    fill: rsHandoffColor.failed,
    ink: rsHandoffColor.retryInk,
    padding: 'handoffRetryButton'
  }
};

const buttonLook = (action: SupportAction): IResultButtonLook =>
  (action.tone && BUTTON_LOOK[action.tone]) || {
    fill: action.tone ? responseTone(action.tone).ink : rsColor.primary,
    ink: rsColor.white,
    padding: 'handoffButton'
  };

// 6473:13232 / 13150 / 13506: 960 x 238, r14, the panel fill, no stroke, content centred. The
// sending card sits 58 under Continue, the sent and failed cards 70.
const Card = styled.section<{ $sending: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-height: ${rsv.size('handoffCardMinHeight')}px;
  margin-top: ${props =>
    rsFor(props).space[props.$sending ? 'handoffSendingTop' : 'handoffResultTop']}px;
  padding: ${rsv.space('resultCard')}px;
  border-radius: ${rsv.radius('panel')}px;
  background: ${rsColor.panel};
  text-align: center;
`;

// 6473:13236: a 52 ring under a 4px #8C83FF stroke.
const Ring = styled.span`
  display: block;
  box-sizing: border-box;
  width: ${rsv.size('handoffSpinner')}px;
  height: ${rsv.size('handoffSpinner')}px;
  border: ${rsv.size('handoffSpinnerStroke')}px solid ${rsHandoffColor.spinner};
  border-radius: 50%;
`;

// 6473:11916 / 11917: a 60 circle at 14%, the glyph Regular 26 on a 31 line.
const Badge = styled.span<{ $ink: string; $fill: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rsv.size('handoffBadge')}px;
  height: ${rsv.size('handoffBadge')}px;
  border-radius: 50%;
  background: ${({ $fill }) => $fill};
  color: ${({ $ink }) => $ink};
  ${responseType('badgeGlyph')}
`;

// 6473:13239 / 11920 / 11962: 22 under the ring, 21 under a badge.
const Title = styled.h4`
  margin: 0;
  color: ${rsHandoffColor.title};
  white-space: pre-line;
  ${responseType('handoffTitle')}

  ${Ring} + & {
    margin-top: ${rsv.space('handoffRingToTitle')}px;
  }

  ${Badge} + & {
    margin-top: ${rsv.space('handoffBadgeToTitle')}px;
  }
`;

// 6473:13242: the lead-in grey, the highlighted counts bold.
const Message = styled.p`
  margin: ${rsv.space('handoffTitleToMessage')}px 0 0;
  color: ${rsColor.secondary};
  white-space: pre-line;
  ${responseType('handoffMessage')}
`;

// 6473:13245 / 13246: a 260 x 4 r4 track. The dump holds only GRADIENT_LINEAR for the fill, so its
// stops are read off the render.
const Track = styled.div`
  width: ${rsv.size('progressTrackWidth')}px;
  max-width: 100%;
  height: ${rsv.size('progressTrackHeight')}px;
  margin-top: ${rsv.space('progressTop')}px;
  border-radius: ${rsv.radius('progress')}px;
  background: ${rsHandoffColor.track};
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, ${rsColor.violet}, ${rsHandoffColor.spinner});
`;

// Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5).
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: ${rsv.space('handoffActionsTop')}px 0 0 -${rsv.space('buttonGap')}px;

  > * {
    margin: ${rsv.space('buttonGap')}px 0 0 ${rsv.space('buttonGap')}px;
  }
`;

const ResultButton = styled(ResponseActionButton)<{ $look: IResultButtonLook }>`
  && {
    justify-content: center;
    min-width: ${rsv.size('handoffButtonMinWidth')}px;
    padding: ${props => rsFor(props).space[props.$look.padding]};
    ${responseType('handoffButton')}
  }

  &&:not(:disabled) {
    background: ${({ $look }) => $look.fill};
    color: ${({ $look }) => $look.ink};
  }
`;

const clampProgress = (progress: number): number => Math.min(100, Math.max(0, progress));

export interface IHandoffResultProps {
  status: GrowthPlannerHandoffStatus;
  result: GrowthPlannerHandoffResult;
  onAction: (action: SupportAction) => void;
}

/** Figma 6473:12607 / 12872 / 13248: the sending, sent and failed cards under the chooser. */
const HandoffResult: React.FC<IHandoffResultProps> = ({ status, result, onAction }) => {
  const titleId = useId();
  const badge = BADGE[status];
  const progress =
    result.progress === null || result.progress === undefined
      ? null
      : clampProgress(result.progress);
  return (
    <Card $sending={status === 'sending'} data-testid="orbit-handoff-result" data-status={status}>
      {status === 'sending' ? (
        <Ring aria-hidden="true" data-testid="orbit-handoff-spinner" />
      ) : null}
      {badge ? (
        <Badge
          $ink={badge.ink}
          $fill={badge.fill}
          aria-hidden="true"
          data-testid="orbit-handoff-badge"
        >
          {badge.glyph}
        </Badge>
      ) : null}
      <Title id={titleId} data-testid="orbit-handoff-result-title">
        {result.title}
      </Title>
      {result.message ? (
        <Message>
          <HighlightedText
            text={result.message}
            highlights={result.highlights}
            ink={rsHandoffColor.title}
            testId="orbit-handoff-highlight"
          />
        </Message>
      ) : null}
      {progress === null ? null : (
        <Track
          role="progressbar"
          aria-labelledby={titleId}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
        >
          <Fill style={{ width: `${progress}%` }} data-testid="orbit-handoff-progress-fill" />
        </Track>
      )}
      {result.actions?.length ? (
        <Actions>
          {result.actions.map(action => (
            <ResultButton
              key={action.id}
              action={action}
              onAction={onAction}
              $look={buttonLook(action)}
            />
          ))}
        </Actions>
      ) : null}
    </Card>
  );
};

export default HandoffResult;
